import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Star, Pencil, ClipboardCheck, ListChecks, TrendingUp, Maximize2, Minimize2, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSbAuth } from "@/context/SupabaseAuthContext";
import { useStore, restaurantStatus, openChecklistsToday, list, getReviews, setReviews, getSalesMix, setSalesMix, hydrate } from "@/lib/dataStore";
import { supabase } from "@/lib/supabaseClient";
import { uploadAttachment, getAttachmentUrl, compressImage } from "@/lib/storage";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const CARD = "bg-card border border-border rounded-2xl shadow-[0_2px_10px_-4px_rgba(20,20,20,0.06)]";

function Ring({ pct, size = 150 }) {
  const r = 62, c = 2 * Math.PI * r;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 150 150" className="h-full w-full -rotate-90">
        <circle cx="75" cy="75" r={r} fill="none" stroke="hsl(var(--secondary))" strokeWidth="12" />
        <circle cx="75" cy="75" r={r} fill="none" stroke="hsl(var(--accent))" strokeWidth="12" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (c * Math.min(pct, 100)) / 100}
          style={{ transition: "stroke-dashoffset 1s cubic-bezier(0.16,1,0.3,1)" }} />
      </svg>
      <div className="absolute inset-0 grid place-content-center text-center">
        <span className="font-display text-4xl font-bold leading-none">{pct}%</span>
        <span className="text-xs text-muted-foreground mt-1">klart idag</span>
      </div>
    </div>
  );
}

export default function LiveDashboard() {
  useStore();
  const { currentRestaurant, hasPermission } = useSbAuth();
  const rid = currentRestaurant?.id;
  const nav = useNavigate();
  const canManage = hasPermission("dashboard.access");
  const [editReviews, setEditReviews] = useState(false);
  const [editSales, setEditSales] = useState(false);
  const [kiosk, setKiosk] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const containerRef = useRef(null);

  // Kiosk mode: full-screen, auto-refreshing wall display for the kitchen.
  useEffect(() => {
    if (!kiosk || !rid) return;
    const id = setInterval(() => { hydrate(rid); setLastRefresh(new Date()); }, 30000);
    return () => clearInterval(id);
  }, [kiosk, rid]);

  const enterKiosk = async () => {
    setKiosk(true);
    try { await containerRef.current?.requestFullscreen?.(); } catch (_) { /* ignore */ }
  };
  const exitKiosk = async () => {
    setKiosk(false);
    try { if (document.fullscreenElement) await document.exitFullscreen(); } catch (_) { /* ignore */ }
  };
  useEffect(() => {
    const onFs = () => { if (!document.fullscreenElement) setKiosk(false); };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const status = rid ? restaurantStatus(rid) : { now: 0, remaining: 0, lastWeekAvg: null };
  const checklists = rid ? openChecklistsToday(rid).slice(0, 6) : [];
  const tasks = rid ? list(rid, "tasks").filter((t) => t.status !== "done").slice(0, 6) : [];
  const reviews = rid ? getReviews(rid) : null;
  const sales = rid ? getSalesMix(rid) : null;

  const today = new Date().toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div
      ref={containerRef}
      className={kiosk
        ? "fixed inset-0 z-50 bg-background overflow-y-auto p-6 sm:p-10"
        : "max-w-[1300px] mx-auto p-5 sm:p-8 animate-fade-up"}
      data-testid="live-dashboard"
    >
      <header className="mb-6 flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className={`font-display font-bold tracking-tight ${kiosk ? "text-4xl sm:text-6xl" : "text-3xl sm:text-4xl"}`}>{currentRestaurant?.name || "Restaurangen"}</h1>
          <p className="text-muted-foreground mt-1 capitalize">{today}</p>
        </div>
        <div className="flex items-center gap-3">
          {kiosk && <span className="text-xs text-muted-foreground tabular-nums" data-testid="kiosk-refreshed">Uppdaterad {lastRefresh.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}</span>}
          {kiosk ? (
            <Button variant="secondary" size="sm" onClick={exitKiosk} data-testid="kiosk-exit" className="rounded-xl">
              <Minimize2 className="h-4 w-4 mr-1.5" />Avsluta kiosk
            </Button>
          ) : (
            <Button variant="secondary" size="sm" onClick={enterKiosk} data-testid="kiosk-enter" className="rounded-xl">
              <Maximize2 className="h-4 w-4 mr-1.5" />Kiosk-läge
            </Button>
          )}
        </div>
      </header>

      <div className={`grid gap-5 lg:grid-cols-3 ${kiosk ? "xl:gap-7" : ""}`}>
        {/* HERO STATUS */}
        <div className={`${CARD} p-7 lg:col-span-1 flex flex-col items-center text-center justify-center`} data-testid="live-status">
          <p className="overline text-muted-foreground mb-4 self-start">Restaurangens status</p>
          <Ring pct={status.now} size={kiosk ? 200 : 150} />
          <p className="mt-5 text-lg font-medium">
            {status.remaining === 0 ? "Allt klart – bra jobbat!" : `${status.remaining} ${status.remaining === 1 ? "checklista" : "checklistor"} kvar`}
          </p>
          <p className="text-sm text-muted-foreground mt-1">Snitt förra veckan: {status.lastWeekAvg != null ? `${status.lastWeekAvg}%` : "samlar data"}</p>
        </div>

        {/* CHECKLISTS */}
        <div className={`${CARD} p-6`} data-testid="live-checklists">
          <p className="overline text-muted-foreground mb-4 flex items-center gap-2"><ListChecks className="h-4 w-4" />Dagens checklistor</p>
          {checklists.length === 0 ? (
            <p className="text-sm text-success font-medium py-6 text-center">Alla checklistor är avklarade ✓</p>
          ) : (
            <div className="space-y-3">
              {checklists.map((c) => (
                <button key={c.id} data-testid={`live-checklist-${c.id}`} onClick={() => !kiosk && nav(`/konto/operation/${c.department}`, { state: { tab: "checklistor" } })}
                  className="w-full text-left group">
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="font-medium truncate">{c.title}</span>
                    <span className="text-muted-foreground tabular-nums shrink-0 ml-2">{c.done}/{c.total}</span>
                  </div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-accent rounded-full transition-all" style={{ width: `${c.total ? (c.done / c.total) * 100 : 0}%` }} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* TASKS */}
        <div className={`${CARD} p-6`} data-testid="live-tasks">
          <p className="overline text-muted-foreground mb-4 flex items-center gap-2"><ClipboardCheck className="h-4 w-4" />Uppgifter</p>
          {tasks.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Inga aktuella uppgifter</p>
          ) : (
            <div className="space-y-2.5">
              {tasks.map((t) => (
                <button key={t.id} data-testid={`live-task-${t.id}`} onClick={() => !kiosk && nav(`/konto/operation/${t.department}`, { state: { tab: "uppgifter" } })}
                  className="w-full text-left flex items-center gap-3 py-1.5">
                  <span className={`h-2 w-2 rounded-full shrink-0 ${t.priority === "high" ? "bg-danger" : t.priority === "medium" ? "bg-warning" : "bg-muted-foreground"}`} />
                  <span className="text-sm truncate flex-1">{t.title}</span>
                  {t.assignee && <span className="text-xs text-muted-foreground shrink-0">{t.assignee}</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* REVIEWS */}
        <div className={`${CARD} p-6`} data-testid="live-reviews">
          <div className="flex items-center justify-between mb-4">
            <p className="overline text-muted-foreground flex items-center gap-2"><Star className="h-4 w-4" />Veckans recensioner</p>
            {canManage && !kiosk && <button onClick={() => setEditReviews(true)} data-testid="edit-reviews" className="text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" /></button>}
          </div>
          {reviews ? (
            <div className="flex items-baseline gap-3">
              <span className="font-display text-5xl font-bold tracking-tight">{Number(reviews.rating).toFixed(1)}</span>
              <div>
                <div className="flex text-warning">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className="h-4 w-4" fill={i <= Math.round(reviews.rating) ? "currentColor" : "none"} />)}</div>
                <p className="text-sm text-muted-foreground mt-1">{reviews.count} nya omdömen</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4">{canManage ? "Lägg till veckans betyg (Qopla + Google)." : "Kommer snart."}</p>
          )}
        </div>

        {/* SALES MIX */}
        <div className={`${CARD} p-6 lg:col-span-2`} data-testid="live-sales">
          <div className="flex items-center justify-between mb-4">
            <p className="overline text-muted-foreground flex items-center gap-2"><TrendingUp className="h-4 w-4" />Förra veckans försäljning</p>
            {canManage && !kiosk && <button onClick={() => setEditSales(true)} data-testid="edit-sales" className="text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" /></button>}
          </div>
          {sales ? (
            <div className="space-y-3">
              {sales.items.map((it, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-sm mb-1.5"><span className="font-medium">{it.name}</span><span className="tabular-nums text-muted-foreground">{it.pct}%</span></div>
                  <div className="h-3 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-accent rounded-full transition-all" style={{ width: `${Math.min(it.pct, 100)}%` }} /></div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4">{canManage ? "Lägg till förra veckans försäljningsfördelning (från Qopla)." : "Kommer snart."}</p>
          )}
        </div>
      </div>

      {editReviews && <ReviewsDialog rid={rid} current={reviews} onClose={() => setEditReviews(false)} />}
      {editSales && <SalesDialog rid={rid} current={sales} onClose={() => setEditSales(false)} />}
    </div>
  );
}

function ReviewsDialog({ rid, current, onClose }) {
  const [rating, setRating] = useState(current?.rating ?? "");
  const [count, setCount] = useState(current?.count ?? "");
  const [scanning, setScanning] = useState(false);

  const scanScreenshot = async (file) => {
    if (!file) return;
    const { data: sess } = await supabase.auth.getSession();
    const token = sess?.session?.access_token;
    if (!token) return toast.error("Din session har gått ut – logga in igen");
    setScanning(true);
    try {
      const compressed = await compressImage(file, 1600, 0.9);
      const att = await uploadAttachment({ restaurantId: rid, entityType: "review", file: compressed });
      const url = await getAttachmentUrl(att.path, 600);
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews-ocr`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ image_url: url }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "AI-avläsning misslyckades");
      if (data.rating) setRating(String(data.rating));
      if (data.count) setCount(String(data.count));
      if (!data.rating && !data.count) toast.warning("AI hittade inget betyg – fyll i manuellt");
      else toast.success(`Avläst: ${data.rating || 0} i betyg · ${data.count || 0} omdömen${data.source ? ` (${data.source})` : ""}`);
    } catch (e) {
      toast.error(e.message || "Kunde inte läsa skärmdumpen");
    } finally {
      setScanning(false);
    }
  };

  const save = () => {
    setReviews(rid, { rating: Number(rating) || 0, count: Number(count) || 0 }, "Ägare");
    toast.success("Recensioner uppdaterade");
    onClose();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="reviews-dialog">
        <DialogHeader><DialogTitle className="font-display">Veckans recensioner</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Mata in snittbetyg och antal nya omdömen (Qopla + Google) – eller ladda upp en skärmdump så läser AI av det automatiskt.</p>

        <label className="mt-2 flex items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-secondary/40 py-3 px-4 cursor-pointer hover:bg-secondary/70 transition-colors text-sm font-medium" data-testid="reviews-upload">
          {scanning ? <><Loader2 className="h-4 w-4 animate-spin" />Läser av…</> : <><Upload className="h-4 w-4" />Läs av skärmdump med AI</>}
          <input type="file" accept="image/*" className="hidden" disabled={scanning}
            onChange={(e) => { scanScreenshot(e.target.files?.[0]); e.target.value = ""; }} data-testid="reviews-file-input" />
        </label>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="space-y-1.5"><Label>Snittbetyg (0–5)</Label><Input type="number" step="0.1" min="0" max="5" value={rating} onChange={(e) => setRating(e.target.value)} data-testid="reviews-rating" /></div>
          <div className="space-y-1.5"><Label>Antal omdömen</Label><Input type="number" min="0" value={count} onChange={(e) => setCount(e.target.value)} data-testid="reviews-count" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="reviews-save">Spara</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SalesDialog({ rid, current, onClose }) {
  const [items, setItems] = useState(current?.items?.length ? current.items.map((i) => ({ ...i })) : [{ name: "Pizza", pct: "" }, { name: "Sallad", pct: "" }, { name: "Kebab", pct: "" }]);
  const upd = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addRow = () => setItems([...items, { name: "", pct: "" }]);
  const save = () => {
    const clean = items.filter((i) => i.name.trim()).map((i) => ({ name: i.name.trim(), pct: Number(i.pct) || 0 }));
    setSalesMix(rid, clean, "Ägare");
    toast.success("Försäljning uppdaterad");
    onClose();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="sales-dialog">
        <DialogHeader><DialogTitle className="font-display">Förra veckans försäljning</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Mata in fördelning i procent (från Qopla). Kopplas automatiskt när POS-integration finns.</p>
        <div className="space-y-2 mt-2">
          {items.map((it, i) => (
            <div key={i} className="flex gap-2">
              <Input value={it.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Produkt" data-testid={`sales-name-${i}`} />
              <Input type="number" min="0" max="100" value={it.pct} onChange={(e) => upd(i, "pct", e.target.value)} placeholder="%" className="w-24" data-testid={`sales-pct-${i}`} />
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={addRow} data-testid="sales-add-row">+ Lägg till rad</Button>
        </div>
        <DialogFooter><Button onClick={save} data-testid="sales-save">Spara</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
